import styled from '@emotion/styled';
import { Button, Dialog, DialogContent } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from '../../i18next';
import { FlexContainerVertical } from '../../layout/Flex';
import { readFileAsDataURL } from '../../services/file';
import { AlertBox } from '../AlertBox';
import { ButtonWithPromise } from '../ButtonWithPromise';
import { DialogActionsWithSlots } from '../DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../DialogTitleWithTypography';
import { Dropzone } from '../Dropzone';
import { cropImage, ImageCrop } from '../ImageCrop';

export const CenteredBody = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

export const ImageUploadDialog = ({
  title,
  open,
  onCancel,
  src,
  onUpload,
  children,
  helperText
}: {
  title: string;
  open: boolean;
  onCancel: () => void;
  src: string;
  onUpload: (nextImage: Blob) => Promise<any>;
  children?: (p: { onClose: () => void }) => React.ReactNode;
  helperText?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [img, setImg] = useState<string | null>(null);
  const [err, setErr] = useState<'INVALID_FILETYPE' | 'UPLOAD_FAILED' | null>(
    null
  );
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<ReactCrop.Crop>({ aspect: 1 });
  const close = () => {
    onCancel();
    setImg(null);
    setImgRef(null);
    setCrop({ aspect: 1 });
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>{title}</DialogTitleWithTypography>
      <DialogContent>
        {err === 'INVALID_FILETYPE' && (
          <AlertBox color="red">
            {t('imageUpload.errors.invalidFileType')}
          </AlertBox>
        )}
        {err === 'UPLOAD_FAILED' && (
          <AlertBox color="red">{t('Something went wrong')}.</AlertBox>
        )}
        <CenteredBody>
          {img ? (
            <ImageCrop
              src={img}
              crop={crop}
              onChange={setCrop}
              onImageLoaded={(image) => {
                setImgRef(image);
                image.width = image.width || 200;
                image.height = image.height || 100;
                setCrop({ height: image.height, width: image.width });
                return false; // Return false when setting crop state in here.
              }}
            />
          ) : (
            <Dropzone
              label={t('imageUpload.dropzone')}
              height={'40vh'}
              onDrop={async (f) => {
                if (!ALLOWED_TYPES.includes(f[0].type)) {
                  setImg(null);
                  setErr('INVALID_FILETYPE');
                  return;
                }
                const nextImg = await readFileAsDataURL(f[0]);
                setImg(nextImg);
                setErr(null);
              }}
            />
          )}
        </CenteredBody>

        {!img && helperText && (
          <FlexContainerVertical marginTop={2} fullWidth>
            {helperText}
          </FlexContainerVertical>
        )}
      </DialogContent>
      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={close}>
              {t('Cancel')}
            </Button>
            {children && children({ onClose: close })}
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={async () => {
                if (!imgRef) {
                  return;
                }
                return cropImage(imgRef, crop)
                  .then(onUpload)
                  .then(close, (err) => {
                    console.log('image upload error', err);
                    return setErr('UPLOAD_FAILED');
                  });
              }}
              pending={t('imageUpload.ctaPending')}
            >
              {t('imageUpload.cta')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};
